import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Mail, MailOpen, MousePointerClick, Send, ArrowDown, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, Spinner, SectionHeader, EmptyState } from "@/components/ui/page";
import { KpiCard } from "@/components/KpiCard";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtNumber, fmtPct, fmtDate } from "@/lib/format";

export default function EmailMarketing() {
  const { selected, isAll, currentClient } = useClient();
  const single = !isAll && selected;

  const { data, isLoading } = useQuery({
    queryKey: ["brevo-summary-page", selected],
    queryFn: async () => (await api.get(`/clients/${selected}/brevo/summary`)).data,
    enabled: !!single,
  });

  if (isAll) return <div><PageHeader title="Email Marketing" /><EmptyState icon={AlertCircle} title="Select a client" subtitle="Choose a specific client to view its email-marketing statistics." /></div>;
  if (isLoading) return <Spinner />;

  if (!data?.configured) {
    return (
      <div>
        <PageHeader title="Email Marketing" subtitle={currentClient?.business_name} />
        <EmptyState icon={Mail} title="Brevo not connected" subtitle="Add this client's Brevo API key in Settings to pull email-marketing statistics." />
      </div>
    );
  }

  if (!data.ok) {
    return (
      <div>
        <PageHeader title="Email Marketing" subtitle={currentClient?.business_name} />
        <div className="bg-white border border-amber-200 rounded-xl p-4 text-sm text-amber-700" data-testid="email-marketing-error">
          Couldn't load Brevo data: {data.error}
        </div>
      </div>
    );
  }

  const t = data.totals || {};
  const campaigns = data.campaigns || [];

  return (
    <div>
      <PageHeader title="Email Marketing" subtitle={currentClient ? `${currentClient.business_name} · Brevo` : "Brevo"} />

      <SectionHeader>Overall (last {data.count} campaigns)</SectionHeader>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard testid="em-sent" label="Emails Sent" icon={Send} accent="blue" value={fmtNumber(t.sent)} showDelta={false} footer={`${data.count} campaigns`} />
        <KpiCard testid="em-delivered" label="Delivered" icon={Mail} accent="slate" value={fmtNumber(t.delivered)} showDelta={false} />
        <KpiCard testid="em-opens" label="Unique Opens" icon={MailOpen} accent="violet" value={fmtNumber(t.unique_opens)} showDelta={false} footer={`${fmtPct(t.open_rate)} open rate`} />
        <KpiCard testid="em-clicks" label="Unique Clicks" icon={MousePointerClick} accent="emerald" value={fmtNumber(t.unique_clicks)} showDelta={false} footer={`${fmtPct(t.click_rate)} click rate`} />
        <KpiCard testid="em-bounces" label="Bounces" icon={ArrowDown} accent="red" value={fmtNumber(t.bounces)} showDelta={false} higherIsBetter={false} />
        <KpiCard testid="em-unsub" label="Unsubscribes" icon={ArrowDown} accent="amber" value={fmtNumber(t.unsubscriptions)} showDelta={false} higherIsBetter={false} />
      </div>

      <SectionHeader>Campaigns</SectionHeader>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Campaign</TableHead>
                <TableHead>Sent Date</TableHead>
                <TableHead className="text-right">Sent</TableHead>
                <TableHead className="text-right">Delivered</TableHead>
                <TableHead className="text-right">Opens</TableHead>
                <TableHead className="text-right">Open Rate</TableHead>
                <TableHead className="text-right">Clicks</TableHead>
                <TableHead className="text-right">Click Rate</TableHead>
                <TableHead className="text-right">Bounces</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">No sent campaigns yet.</TableCell></TableRow>
              ) : campaigns.map((c) => (
                <TableRow key={c.id} data-testid={`campaign-row-${c.id}`}>
                  <TableCell className="max-w-[280px]">
                    <div className="font-semibold text-slate-800 truncate">{c.name || "Untitled"}</div>
                    {c.subject && <div className="text-xs text-slate-500 truncate">{c.subject}</div>}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap">{c.sentDate ? fmtDate(c.sentDate) : "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNumber(c.sent)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNumber(c.delivered)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNumber(c.unique_opens)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold text-violet-700">{fmtPct(c.open_rate)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNumber(c.unique_clicks)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold text-emerald-700">{fmtPct(c.click_rate)}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-500">{fmtNumber(c.bounces)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-3">Open/click rates are calculated on delivered emails. Data pulled live from Brevo (marketing campaigns).</p>
      <div className="h-6" />
    </div>
  );
}
